import React, { useState } from 'react';

// Formats the cutoff the counts endpoint sends back ("2026-05-05T00:00:00") as
// "2026.05.05 0:00". Parsed by hand rather than through Date: the backend runs
// USE_TZ = False, so the value is a naive local timestamp and handing it to
// Date() only invites a browser-dependent shift of the very number the tooltip
// exists to state precisely.
function formatCutoff(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(iso));
    if (!m) return null;
    const [, y, mo, d, h, min] = m;
    return `${y}.${mo}.${d} ${parseInt(h, 10)}:${min}`;
}

// The info icon beside the "To Link" tab count, explaining that the number
// covers only orders placed after the platform's ERP rollout — without it a
// badge reading 0 while the list below holds thousands of rows looks like a bug.
//
// Same treatment as the Platform Listing stock-inconsistency tooltip
// (StockInconsistencyBadge) and FinalResultRuleTooltip: a 10px filled info
// glyph and a compact #495057 bubble with a shadow and an 8px arrow, sitting
// 8px above the icon. The bubble is position:fixed against the icon's viewport
// rect so it escapes the tab strip rather than being clipped by it.
//
// `color` keeps the glyph tracking the tab's own text colour (dark on the
// active yellow pill, grey when inactive); it defaults to the #5E6E82 that
// Platform Listing uses.
export default function ToLinkCutoffTooltip({ cutoff, color = '#5E6E82' }) {
    const [tip, setTip] = useState(null);
    const label = formatCutoff(cutoff);

    if (!label) return null;

    return (
        <>
            {/* alignSelf:center keeps the glyph on the tab's vertical midline —
                it is shorter than the label and the count badge beside it. */}
            <span
                style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'center', cursor: 'pointer', marginLeft: 2, flexShrink: 0 }}
                onMouseEnter={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    setTip({ left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 8 });
                }}
                onMouseLeave={() => setTip(null)}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375 6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594 6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594 2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688 8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322 7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z" fill={color} />
                    <path d="M5.58122 4.11768L4.14997 4.29705L4.09872 4.53455L4.37997 4.58643C4.56372 4.63018 4.59997 4.69643 4.55997 4.87955L4.09872 7.04705C3.97747 7.60768 4.16435 7.87143 4.60372 7.87143C4.94435 7.87143 5.33997 7.71393 5.51935 7.49768L5.57434 7.23768C5.44934 7.34768 5.26684 7.39143 5.1456 7.39143C4.97372 7.39143 4.91122 7.2708 4.9556 7.0583L5.58122 4.11768Z" fill={color} />
                    <path d="M5 3.4375C5.34518 3.4375 5.625 3.15768 5.625 2.8125C5.625 2.46732 5.34518 2.1875 5 2.1875C4.65482 2.1875 4.375 2.46732 4.375 2.8125C4.375 3.15768 4.65482 3.4375 5 3.4375Z" fill={color} />
                </svg>
            </span>
            {/* span, not div: this renders inside the tab's <button>, which only
                accepts phrasing content. */}
            {tip && (
                <span style={{
                    display: 'block',
                    position: 'fixed', left: tip.left, bottom: tip.bottom,
                    transform: 'translateX(-50%)',
                    background: '#495057', color: '#FFFFFF', padding: '10px 12px',
                    borderRadius: '8px', zIndex: 9999, whiteSpace: 'nowrap',
                    boxShadow: '0px 4px 20px rgba(0,0,0,0.32)',
                    pointerEvents: 'none', fontFamily: 'Montserrat', fontSize: '12px',
                    fontWeight: 400, lineHeight: 1.4,
                }}>
                    Orders to be linked after {label}
                    <span style={{
                        display: 'block',
                        width: 0, height: 0, position: 'absolute', left: '50%', bottom: '-8px',
                        transform: 'translateX(-50%)',
                        borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
                        borderTop: '8px solid #495057',
                    }} />
                </span>
            )}
        </>
    );
}
